import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import Skeleton from '@mui/material/Skeleton';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import * as yup from 'yup';
import { useFormik } from 'formik';
import _ from 'lodash';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Restricted } from '../Restricted';

import {
  GetConsumerDetailedQuery,
  useListFriendsQuery,
  useSearchFriendsLazyQuery,
  useHandleFriendMutation,
  ListFriendsQuery,
  SearchFriendsQuery,
} from '@graphql';
import { useSession, useToastError } from '@hooks';
import { truncateText } from '@utils';
import { EmptyState } from '../../common';

interface FriendsProps {
  consumer: GetConsumerDetailedQuery['consumer'];
}

function Friends(props: Readonly<FriendsProps>) {
  const { consumer } = props;
  const { account } = useSession();
  const theme = useTheme();
  const [_searchParams, setSearchParams] = useSearchParams();
  const [isSearching, setIsSearching] = React.useState(false);
  const [friends, setFriends] = React.useState<ListFriendsQuery['listFriends']['active']['list']>([]);
  const [pendingFriends, setPendingFriends] =
    React.useState<ListFriendsQuery['listFriends']['pending']>(null);
  const [searchResult, setSearchResult] = React.useState<SearchFriendsQuery['searchFriends']>([]);
  const { t } = useTranslation('cta');
  const isMe = consumer.id === account?.id;

  const [searchFriends, { loading: searchLoading }] = useSearchFriendsLazyQuery({
    onCompleted: ({ searchFriends }) => {
      setSearchResult(searchFriends);
    },
  });

  const [handleFriend] = useHandleFriendMutation({
    onError: useToastError,
  });

  const { loading } = useListFriendsQuery({
    skip: !consumer?.id,
    variables: { accountId: consumer.id },
    onCompleted: ({ listFriends }) => {
      setFriends(listFriends.active.list);

      if (isMe && listFriends.pending) {
        setPendingFriends(listFriends.pending);
      }
    },
  });

  const formik = useFormik({
    initialValues: {
      text: '',
    },
    validationSchema: yup.object({
      text: yup.string(),
    }),
    onSubmit: (values) => {
      searchFriends({ variables: { text: values.text } });
    },
  });

  React.useEffect(() => {
    setIsSearching(formik.values.text.length > 0);

    if (formik.values.text.length > 0) {
      handleSearch(formik.values.text);
    }
  }, [formik.values.text]);

  // do no spam the API
  const handleSearch = React.useCallback(
    _.debounce((searchValue: string) => {
      formik.submitForm();
    }, 300),
    []
  );

  const handleClickLink = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const slug = (e.currentTarget as HTMLElement).dataset.slug;
    setSearchParams({ c: slug ?? '' }, { preventScrollReset: true });
  };

  // add near your other handlers
  const handleClickAction = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const target = e.currentTarget as HTMLElement;
    const { friendId, action } = target.dataset;

    if (friendId && action) {
      handleFriend({ variables: { accountId: friendId, action } });
    }
  };

  if (!consumer.isMyFriend) {
    return (
      <Stack spacing={5} p={3} height="100%" alignItems="center" justifyContent="center">
        <Restricted />
      </Stack>
    );
  }

  if (consumer.id !== account?.id) {
    return null;
  }

  return (
    <Stack spacing={5} p={3}>
      {isMe && pendingFriends && pendingFriends.list.filter(({ isSent }) => !isSent).length > 0 && (
        <Stack spacing={2}>
          <Stack
            spacing={3}
            justifyContent="space-between"
            sx={{ border: `1px solid ${theme.palette.grey[800]}`, borderRadius: 1, p: 2 }}
          >
            <Typography fontWeight="bold">{t('text.New requests', { ns: 'account' })}</Typography>
            <Stack
              spacing={1}
              p={2}
              justifyContent="space-between"
              sx={{ backgroundColor: 'background.paper', borderRadius: 1 }}
            >
              <Typography variant="body2" fontSize=".7rem" color="textSecondary">
                {t(
                  'text.A notification is sent to the person who made the request only when you accept it, never when you decline.',
                  { ns: 'account' }
                )}
              </Typography>
            </Stack>
            <Stack spacing={2} divider={<Divider />}>
              {pendingFriends.list
                .filter(({ isSent }) => !isSent)
                .map(({ friend }) => (
                  <Link
                    key={friend.id}
                    to={`?c=${friend.slug}`}
                    data-slug={friend.slug}
                    style={{ textDecoration: 'none', width: '100%' }}
                    onClick={handleClickLink}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" width="100%" divider={<Divider />}>
                      <Avatar
                        src={friend.avatar?.url}
                        alt={friend.name}
                        sx={{ width: { xs: 30, sm: 50 }, height: { xs: 30, sm: 50 } }}
                      />
                      <Stack width="100%">
                        <Typography fontWeight="bold" fontSize={{ xs: '.8rem', sm: '.9rem' }}>
                          {truncateText(friend.name, 25)}
                        </Typography>
                        <Typography color="textSecondary" fontSize={{ xs: '.7rem', sm: '.8rem' }}>
                          @{truncateText(friend.slug, 25)}
                        </Typography>
                      </Stack>
                      <Stack spacing={2} direction="row" alignItems="center">
                        <IconButton
                          data-friend-id={friend.id}
                          data-action="reject"
                          onClick={handleClickAction}
                          sx={{
                            width: { xs: 30, sm: 40 },
                            height: { xs: 30, sm: 40 },
                            border: '1px solid',
                            borderColor: 'grey.500',
                          }}
                        >
                          <theme.icons.close sx={{ width: { xs: 20, sm: 30 }, height: { xs: 20, sm: 30 } }} />
                        </IconButton>
                        <IconButton
                          data-friend-id={friend.id}
                          data-action="accept"
                          onClick={handleClickAction}
                          sx={{
                            width: { xs: 30, sm: 40 },
                            height: { xs: 30, sm: 40 },
                            backgroundColor: 'green',
                            '&:hover': { backgroundColor: 'darkgreen' },
                          }}
                        >
                          <theme.icons.check
                            sx={{
                              color: 'lightgrey',
                              '&:hover': { color: 'white' },
                              width: { xs: 20, sm: 30 },
                              height: { xs: 20, sm: 30 },
                            }}
                          />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Link>
                ))}
            </Stack>
          </Stack>
        </Stack>
      )}
      {isMe && (
        <TextField
          name="text"
          fullWidth
          size="small"
          placeholder={t('search', { ns: 'cta' })}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.text && Boolean(formik.errors.text)}
          helperText={formik.touched.text && Boolean(formik.errors.text) ? String(formik.errors.text) : null}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <theme.icons.search sx={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            },
          }}
        />
      )}
      {
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} justifyContent="space-between">
            <Typography>{t(isSearching ? 'word.Results' : 'word.Friends', { ns: 'common' })}</Typography>
          </Stack>
          {(!isSearching && friends.length === 0) || (isSearching && searchResult.length === 0) ? (
            <EmptyState
              text={
                isSearching
                  ? t('word.No result', { ns: 'common' })
                  : t('text.No friends yet, use the search above to find them', { ns: 'account' })
              }
            />
          ) : null}
          {(isSearching ? searchResult : friends).map(({ friend }) => (
            <Link
              key={friend.id}
              to={`?type=consumer&q=${friend.slug}`}
              data-slug={friend.slug}
              style={{ textDecoration: 'none' }}
              onClick={handleClickLink}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar src={friend.avatar?.url} alt={friend.name} sx={{ width: 50, height: 50 }} />
                <Stack>
                  <Typography fontWeight="bold" fontSize={{ xs: '.8rem', sm: '.7rem' }}>
                    {truncateText(friend.name, 25)}
                  </Typography>
                  <Typography color="textSecondary" fontSize={{ xs: '.8rem', sm: '.7rem' }}>
                    @{truncateText(friend.slug, 25)}
                  </Typography>
                </Stack>
              </Stack>
            </Link>
          ))}
        </Stack>
      }
      {(searchLoading || loading) && [...Array(5)].map((_, idx) => <FriendSkeleton key={idx} />)}
    </Stack>
  );
}

const FriendSkeleton = () => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Skeleton variant="circular" width={50} height={50} />
    <Stack>
      <Skeleton variant="text" width={250} />
      <Skeleton variant="text" width={150} />
    </Stack>
  </Stack>
);

export default React.memo(Friends);
